<script setup lang="ts">
import UIHeader from '@/components/ui/UIHeader.vue'
import UIModal, {
  UIModalBody,
  UIModalCancelButton,
  UIModalFooter,
  UIModalSubmitButton
} from '@/components/ui/UIModal'
import { useDetectBreakpoint } from '@/composables/useDetectBreakpoint'
import useLanguage from '@/composables/useLanguage'
import { useConfirmStore } from '@/store/confirm'
import { PhTrash } from '@phosphor-icons/vue'
import { computed, onMounted, ref } from 'vue'

const emit = defineEmits<{
  (e: 'confirm'): void
  (e: 'cancel'): void
}>()

const i18n = useLanguage('UIModal')
const confirmStore = useConfirmStore()
const firstElement = ref<typeof UIModalCancelButton | undefined>()

const isFullScreen = computed(
  () => useDetectBreakpoint('xs') && confirmStore.fullScreenOnMobile
)

const onSubmit = (evt) => {
  emit('confirm')
  evt.preventDefault()
}

onMounted(() => {
  firstElement.value?.$el.focus()
})
</script>

<template>
  <UIModal
    v-if="confirmStore.isOpen"
    id="confirm-modal"
    :label-id="`confirm-modal-header-${confirmStore.key}`"
    :description-id="`confirm-modal-description-${confirmStore.key}`"
    :full-screen="isFullScreen"
  >
    <div class="flex flex-col h-full">
      <UIHeader
        :id="`confirm-modal-header-${confirmStore.key}`"
        :text="confirmStore.header"
        :variant="confirmStore.variant"
        :btn-variant="isFullScreen ? 'close' : undefined"
        @btn-clicked="$emit('cancel')"
      />
      <form class="flex flex-col grow min-h-0" @submit="onSubmit">
        <UIModalBody
          :id="`confirm-modal-description-${confirmStore.key}`"
          variant="coloredHeader"
          class="grow min-h-0 overflow-y-auto text-body text-default"
          >{{ confirmStore.text }}</UIModalBody
        >
        <UIModalFooter>
          <UIModalCancelButton ref="firstElement" @click="$emit('cancel')" />
          <UIModalSubmitButton
            variant="filled"
            :color="
              confirmStore.variant == 'destructive' ? 'destructive' : 'primary'
            "
            class="flex items-center whitespace-nowrap"
          >
            <PhTrash
              v-if="
                confirmStore.confirmBtn === 'delete' ||
                confirmStore.confirmBtn === 'remove'
              "
              :size="16"
            />
            {{ i18n[confirmStore.confirmBtn] }}
          </UIModalSubmitButton>
        </UIModalFooter>
      </form>
    </div>
  </UIModal>
</template>
